import { useCallback, useMemo, type ReactNode } from "react";

import { useNotifications } from "./useNotifications/index.js";

/**
 * Toasts with this application's default dwell time.
 *
 * The returned object is stable across renders so it can be named in an
 * effect's dependency list. Returning a fresh one each time would re-run any
 * such effect on every render, which for an effect that itself shows a toast
 * is an endless loop.
 */
export const useToastNotifications = () => {
  const notifications = useNotifications();

  const show = useCallback(
    (
      message: ReactNode,
      options?: {
        severity?: "success" | "error" | "warning" | "info";
        autoHideDuration?: number;
      },
    ) => {
      return notifications.show(message, {
        severity: options?.severity,
        autoHideDuration: options?.autoHideDuration ?? 30000,
      });
    },
    [notifications],
  );

  const close = useCallback(
    (key: string) => {
      notifications.close(key);
    },
    [notifications],
  );

  return useMemo(() => ({ show, close }), [show, close]);
};
